// The Studio's views: one project, four lenses on it.
//
// The project is the room document (EditorDoc) — its room, its system, its
// placement, its treatment. Every view reads and edits the SAME document, so
// moving a speaker on the Plan moves it in the Room, and choosing an amplifier
// in the System re-checks its power against the seat the Plan put it at.
// Switching views never switches projects, and never loses a selection.
//
//   system  the parts list — what you are building, what it costs, and
//           whether it works together (free: design)
//   room    "3D" — the room in three dimensions, the scene you walk around
//           (free to place and look; simulation overlays when computed)
//   plan    "2D" — the precise drawing: top and side views, dimensions, the
//           listening triangle, reflection points, measured as you drag
//   sound   the acoustics — simulation, analysis, auralization, EQ
//
// The two editing lenses are named for how they show the room, not for what
// they edit: both edit the room, so "Room" said nothing about the 3D view and
// "Plan" hid that the drawing has a side view too (owner, 2026-09-27). The
// ids stay `room` / `plan` — they are in every shared link (`?view=`).
//
// `Record<StudioView, …>` so a view added to the union without an entry here
// is a compile error rather than a blank lens (AGENTS.md registry rule).

import { AudioWaveform, Box, DraftingCompass, ListChecks, type LucideIcon } from 'lucide-react';

export type StudioView = 'system' | 'room' | 'plan' | 'sound';

/** Left to right, the order a design tends to travel in. */
export const STUDIO_VIEW_ORDER: readonly StudioView[] = ['system', 'room', 'plan', 'sound'];

export const STUDIO_VIEWS: Record<StudioView, { label: string; icon: LucideIcon; shortcut: string }> = {
  system: { label: 'System', icon: ListChecks, shortcut: '1' },
  room: { label: '3D', icon: Box, shortcut: '2' },
  plan: { label: '2D', icon: DraftingCompass, shortcut: '3' },
  sound: { label: 'Sound', icon: AudioWaveform, shortcut: '4' },
};

/** The lenses in `ViewSwitch`'s item shape — one list for the header's switch
 *  and the phone's bottom bar, so the two can never offer different lenses. */
export const STUDIO_VIEW_ITEMS = STUDIO_VIEW_ORDER.map((value) => ({
  value, label: STUDIO_VIEWS[value].label, icon: STUDIO_VIEWS[value].icon,
}));

/** The lens a project opens on when nothing asks for another: the room is the
 *  hero (PRODUCT.md, "The room is the hero"). */
export const DEFAULT_STUDIO_VIEW: StudioView = 'room';

export function isStudioView(value: unknown): value is StudioView {
  return typeof value === 'string' && (STUDIO_VIEW_ORDER as readonly string[]).includes(value);
}

/** The URL parameter that carries the lens, so any view of any project is a
 *  link — "anywhere in two clicks" includes a link someone sent you. */
export const STUDIO_VIEW_PARAM = 'view';
